import React, { useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import CloseIcon from 'icons/close.svg';
import FolderIcon from 'icons/folder.svg';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import voca from 'voca';

import useDebounce from 'components/hooks/useDebounce';

import CircleQuestion from 'components/application/CircleQuestion';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import FolderSelect from 'components/folders/FolderSelect';

import {
  formatInfiniteData,
  getNextPageParam,
} from 'components/helpers/reactQuery';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function FolderInputSelect({ domainFolder }) {
  const {
    division: {
      attributes: { singularRAMSName },
    },
  } = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectForm.FolderInputSelect',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const [isSelectMenuOpen, setIsSelectMenuOpen] = useState(false);
  const [searchParam, setSearchParam] = useState('');

  const [selectedFolderId, setSelectedFolderId] = useState(
    domainFolder?.id || '',
  );

  const [debouncedSearchParam, _resetDebouncedSearchParam] = useDebounce(
    searchParam,
    250,
  );

  const [loadMoreFoldersRef, isLoadMoreFoldersInView] = useInView();

  const {
    data: folders,
    fetchNextPage: fetchFoldersNextPage,
    hasNextPage: hasFoldersNextPage,
    isFetching: isFetchingFolders,
  } = useInfiniteQuery({
    enabled:
      isSelectMenuOpen ||
      (!!selectedFolderId && selectedFolderId !== domainFolder?.id),
    getNextPageParam,
    keepPreviousData: true,
    queryKey: ['folders', { search: debouncedSearchParam }],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await axios.post('/folder_searches', {
        folder_search: {
          page: pageParam,
          search: debouncedSearchParam,
        },
      });

      return response.data;
    },
    select: (data) => formatInfiniteData({ data }),
  });

  const selectedFolder =
    selectedFolderId ?
      selectedFolderId === domainFolder?.id ?
        domainFolder
      : folders?.data.find((folder) => folder.id === selectedFolderId)
    : null;

  const shouldLoadMoreFolders =
    !isFetchingFolders &&
    folders?.data.length > 0 &&
    hasFoldersNextPage &&
    isLoadMoreFoldersInView;

  if (shouldLoadMoreFolders) fetchFoldersNextPage();

  return (
    <>
      {selectedFolder ?
        <div className='tw-flex tw-items-center tw-gap-1'>
          <div className='tw-flex tw-h-10 tw-w-[80%] tw-items-center tw-gap-2 tw-rounded-lg tw-bg-blue-025 tw-px-3'>
            <FolderIcon
              className='[&_path]:tw-fill-grey-700'
              height={20}
              width={20}
            />
            <p className='tw-mb-0 tw-truncate tw-text-m tw-font-normal tw-text-grey-900'>
              {selectedFolder.attributes.name}
            </p>
          </div>
          <button
            className='tw-h-5 tw-w-5 tw-border-0 tw-bg-white tw-p-0'
            onClick={() => {
              setSearchParam('');
              setSelectedFolderId('');
            }}
          >
            <CloseIcon
              className='[&_path]:tw-fill-grey-500'
              height={20}
              width={20}
            />
            <span className='tw-sr-only'>{i18nT('deselect')}</span>
          </button>
        </div>
      : <div className='tw-flex tw-items-center tw-gap-2'>
          <div className='tw-w-[80%]'>
            <FolderSelect
              folders={folders?.data || []}
              isLoading={isFetchingFolders}
              loadMoreRef={loadMoreFoldersRef}
              noOptionsMessage={
                <>
                  <p className='tw-mb-0 tw-text-grey-400'>
                    {debouncedSearchParam ?
                      i18nTShared('noMatchingResults')
                    : i18nT('select.noOptionsMessage.noFolders')}
                  </p>
                  <p className='tw-mb-0 tw-text-grey-400'>
                    {i18nT('select.noOptionsMessage.blank')}
                  </p>
                  <a
                    className='tw-font-medium tw-text-blue-500'
                    href='https://intercom.help/hands-hq/en/articles/11154401-folders-organise-and-group-your-rams'
                    rel='noreferrer'
                    target='_blank'
                  >
                    {i18nT('select.noOptionsMessage.learnMore', {
                      interpolation: { escapeValue: false },
                      singularRAMSName,
                    })}
                  </a>
                </>
              }
              onInputChange={(value, actionMeta) => {
                // When the action is set-value (selecting an option), the value will be '' and this will trigger a folders fetch with an empty search param.
                // The selected folder may no longer be found via the query (depending on pagination).
                if (actionMeta.action !== 'set-value') setSearchParam(value);
              }}
              onMenuClose={() => setIsSelectMenuOpen(false)}
              onMenuOpen={() => setIsSelectMenuOpen(true)}
              onSelect={setSelectedFolderId}
            />
          </div>
          <Tooltip
            placement='bottom'
            tooltip={i18nT('tooltip', {
              interpolation: { escapeValue: false },
              singularRAMSName: voca.capitalize(singularRAMSName),
            })}
            trigger='hover'
          >
            <CircleQuestion />
          </Tooltip>
        </div>
      }
      <input name='project[folder_id]' type='hidden' value={selectedFolderId} />
    </>
  );
}

FolderInputSelect.propTypes = {
  domainFolder: resourceShape('folder'),
};
